import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Box, ButtonBase, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { SET_THEME_MODE } from '../../../store/actions';
import ProfileMenu from '../Header/ProfileSection/ProfileMenu';
import NotificationSection from '../Header/NotificationSection';
import { getFullName, getInitials, getRoleLabel } from '../Header/ProfileSection/userInfo';
import { IconMoon, IconSelector, IconSun } from '@tabler/icons';

const SidebarFooter = ({ userData, collapsed }) => {
  const dispatch = useDispatch();
  const navType = useSelector((state) => state.customization.navType);
  const isDark = navType === 'dark';
  const setMode = (mode) => dispatch({ type: SET_THEME_MODE, navType: mode });

  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const fullName = getFullName(userData);
  const roleLabel = getRoleLabel(userData);

  const userButtonSx = {
    borderRadius: '10px',
    transition: 'background-color .15s ease',
    '&:hover': { bgcolor: 'action.hover' },
    '&.Mui-focusVisible': { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.light}` }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, alignItems: collapsed ? 'center' : 'stretch' }}>
      {collapsed ? (
        <Tooltip title={isDark ? 'Šviesi tema' : 'Tamsi tema'} placement="right">
          <IconButton
            aria-label={isDark ? 'Perjungti į šviesią temą' : 'Perjungti į tamsią temą'}
            onClick={() => setMode(isDark ? 'light' : 'dark')}
            sx={{ width: 44, height: 38, bgcolor: 'action.hover', color: 'text.secondary' }}
          >
            {isDark ? <IconSun stroke={1.8} size={18} /> : <IconMoon stroke={1.8} size={18} />}
          </IconButton>
        </Tooltip>
      ) : (
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={isDark ? 'dark' : 'light'}
          onChange={(_, v) => v && setMode(v)}
          aria-label="Tema"
        >
          <ToggleButton value="light" sx={{ gap: 0.75 }}>
            <IconSun stroke={1.8} size={15} />
            Šviesi
          </ToggleButton>
          <ToggleButton value="dark" sx={{ gap: 0.75 }}>
            <IconMoon stroke={1.8} size={15} />
            Tamsi
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {collapsed && <NotificationSection variant="sidebar-collapsed" userData={userData} />}
      {collapsed ? (
        <Tooltip title={fullName || 'Paskyra'} placement="right">
          <ButtonBase
            ref={anchorRef}
            onClick={() => setMenuOpen(true)}
            aria-label="Paskyra"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            sx={{ ...userButtonSx, width: 44, height: 44 }}
          >
            <Avatar sx={{ width: 34, height: 34, fontSize: 12, fontWeight: 700 }}>{getInitials(userData)}</Avatar>
          </ButtonBase>
        </Tooltip>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <ButtonBase
          ref={anchorRef}
          onClick={() => setMenuOpen(true)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          sx={{ ...userButtonSx, flexGrow: 1, minWidth: 0, justifyContent: 'flex-start', gap: 1.25, p: '6px', textAlign: 'left' }}
        >
          <Avatar sx={{ width: 34, height: 34, fontSize: 12, fontWeight: 700 }}>{getInitials(userData)}</Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }} noWrap>
              {fullName || '…'}
            </Typography>
            {roleLabel && (
              <Typography sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.3 }} noWrap>
                {roleLabel}
              </Typography>
            )}
          </Box>
          <Box component="span" sx={{ display: 'flex', color: 'text.disabled', flexShrink: 0 }}>
            <IconSelector stroke={1.8} size={16} />
          </Box>
        </ButtonBase>
        <NotificationSection variant="sidebar" userData={userData} />
        </Box>
      )}

      <ProfileMenu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        userData={userData}
        anchorOrigin={collapsed ? { vertical: 'bottom', horizontal: 'right' } : { vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={collapsed ? { ml: 1 } : { mt: -1 }}
      />
    </Box>
  );
};

SidebarFooter.propTypes = {
  userData: PropTypes.object,
  collapsed: PropTypes.bool
};

export default SidebarFooter;